"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useEvents } from "./pusher";
import { DeploymentBuildUpdateSchema } from "skedyul-core/features/deployments/resources/types";
import type { BuildLogEntry } from "skedyul-core/features/workflows/resources/types";
import { store, type RootState } from "@store/index";
import {
  getDeployment,
  applyDeploymentBuildUpdate,
  clearDeployment,
  setPendingMigration,
  type MigrationApprovalRequired,
} from "@store/features/deployments/deploymentsSlice";
import { z } from "zod/v4";

// Schema for the new deployment.log event from CloudWatch streaming
const DeploymentLogEventSchema = z.object({
  deploymentId: z.string().optional(),
  provisionId: z.string().optional(),
  entries: z.array(
    z.object({
      timestamp: z.string(),
      level: z.enum(["info", "warn", "error", "debug"]),
      phase: z.enum(["build", "provision", "migration"]),
      type: z.enum(["log", "step", "approval_required", "approval_resolved"]),
      msg: z.string(),
      step: z
        .object({
          name: z.string(),
          status: z.enum([
            "pending",
            "in_progress",
            "awaiting_approval",
            "completed",
            "failed",
          ]),
          startedAt: z.string().optional(),
          completedAt: z.string().optional(),
          errorMessage: z.string().optional(),
        })
        .optional(),
      migration: z
        .object({
          migrationId: z.string(),
          impacts: z.array(z.any()),
          timeoutMinutes: z.number(),
          createdAt: z.string(),
          status: z
            .enum(["pending", "approved", "denied", "expired", "failed"])
            .optional(),
          resolvedAt: z.string().optional(),
          resolvedBy: z.string().optional(),
        })
        .optional(),
    }),
  ),
});

/**
 * DeploymentsProvider handles Pusher subscriptions for deployment build updates.
 * It tracks which deployments are being watched and subscribes to their channels.
 */
export function DeploymentsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Track which deployment IDs are being watched (with ref counts)
  const [tracked, setTracked] = useState<Map<string, number>>(() => new Map());
  const trackedIds = useMemo(() => Array.from(tracked.keys()), [tracked]);

  // Handle incoming Pusher events and dispatch to Redux
  const handleDeploymentEvent = useCallback(
    (rawData: unknown) => {
      // Pusher wraps the payload in a "payload" property
      const env =
        typeof rawData === "object" && rawData !== null
          ? (rawData as Record<string, unknown>)
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : rawData;

      const parsed = DeploymentBuildUpdateSchema.safeParse(payload);
      if (!parsed.success) return;

      const { deploymentId, logs, status } = parsed.data;
      if (!deploymentId || !tracked.has(deploymentId)) {
        return;
      }

      // Dispatch to Redux slice
      store.dispatch(
        applyDeploymentBuildUpdate({
          deploymentId,
          logs: logs as BuildLogEntry[] | undefined,
          status,
        }),
      );
    },
    [tracked],
  );

  // Handle migration approval required events
  const handleMigrationApprovalEvent = useCallback(
    (rawData: unknown) => {
      const env =
        typeof rawData === "object" && rawData !== null
          ? (rawData as Record<string, unknown>)
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : rawData;

      if (!payload || typeof payload !== "object") return;

      const data = payload as MigrationApprovalRequired;
      if (!data.deploymentId || !tracked.has(data.deploymentId)) {
        return;
      }

      // Dispatch to Redux slice
      store.dispatch(setPendingMigration(data));
    },
    [tracked],
  );

  // Handle new deployment.log events (from CloudWatch streaming)
  const handleDeploymentLogEvent = useCallback(
    (rawData: unknown) => {
      const env =
        typeof rawData === "object" && rawData !== null
          ? (rawData as Record<string, unknown>)
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : rawData;

      const parsed = DeploymentLogEventSchema.safeParse(payload);
      if (!parsed.success) return;

      const { deploymentId, entries } = parsed.data;
      if (!deploymentId || !tracked.has(deploymentId)) {
        return;
      }

      // Convert entries to BuildLogEntry format for Redux
      const logs: BuildLogEntry[] = entries.map((entry) => ({
        timestamp: entry.timestamp,
        level: entry.level,
        message: entry.msg,
      }));

      // Check for migration approval entries
      for (const entry of entries) {
        if (entry.type === "approval_required" && entry.migration) {
          store.dispatch(
            setPendingMigration({
              deploymentId,
              migrationId: entry.migration.migrationId,
              impacts: entry.migration.impacts,
              timeoutMinutes: entry.migration.timeoutMinutes,
              createdAt: entry.migration.createdAt,
            }),
          );
        } else if (entry.type === "approval_resolved") {
          // Clear pending migration when resolved
          store.dispatch(setPendingMigration(null));
        }
      }

      // Dispatch logs to Redux
      if (logs.length > 0) {
        store.dispatch(
          applyDeploymentBuildUpdate({
            deploymentId,
            logs,
          }),
        );
      }
    },
    [tracked],
  );

  // Create Pusher subscriptions for all tracked deployments
  const subscriptions = useMemo(
    () =>
      trackedIds.map((deploymentId) => ({
        channel: ({ subdomain }: { subdomain?: string | null }) =>
          subdomain ? `private-${subdomain}-deployment-${deploymentId}` : "",
        events: {
          "deployment.build.updated": handleDeploymentEvent,
          "deployment.log": handleDeploymentLogEvent,
          "deployment.migration.approval_required":
            handleMigrationApprovalEvent,
        },
      })),
    [
      trackedIds,
      handleDeploymentEvent,
      handleDeploymentLogEvent,
      handleMigrationApprovalEvent,
    ],
  );

  useEvents(subscriptions);

  // Expose tracking functions via context
  const contextValue = useMemo(
    () => ({
      trackDeployment: (deploymentId: string) => {
        if (!deploymentId) return;
        setTracked((prev) => {
          const next = new Map(prev);
          const current = next.get(deploymentId) ?? 0;
          next.set(deploymentId, current + 1);
          return next;
        });
      },
      untrackDeployment: (deploymentId: string) => {
        if (!deploymentId) return;
        setTracked((prev) => {
          const current = prev.get(deploymentId);
          if (typeof current !== "number") return prev;
          const next = new Map(prev);
          if (current <= 1) {
            next.delete(deploymentId);
          } else {
            next.set(deploymentId, current - 1);
          }
          return next;
        });
      },
    }),
    [],
  );

  return (
    <DeploymentsContext.Provider value={contextValue}>
      {children}
    </DeploymentsContext.Provider>
  );
}

// Context for tracking functions only
type DeploymentsContextValue = {
  trackDeployment: (deploymentId: string) => void;
  untrackDeployment: (deploymentId: string) => void;
};

const DeploymentsContext = React.createContext<DeploymentsContextValue>({
  trackDeployment: () => {},
  untrackDeployment: () => {},
});

export function useDeploymentsContext(): DeploymentsContextValue {
  return React.useContext(DeploymentsContext);
}

/** Fallback polling while deployment is in progress (when Pusher may miss events). */
function useDeploymentLogPolling(deploymentId?: string | null) {
  const prevStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!deploymentId) return;

    const pollInterval = setInterval(() => {
      const currentDeployment = store.getState().deployments.deployment.data;

      if (currentDeployment?.id !== deploymentId) {
        void store.dispatch(getDeployment({ deploymentId, silent: true }));
        return;
      }

      const status = currentDeployment.status;
      const wasInProgress =
        prevStatusRef.current === "QUEUED" ||
        prevStatusRef.current === "BUILDING";
      const isTerminal = status === "SUCCEEDED" || status === "FAILED";

      if (status === "QUEUED" || status === "BUILDING") {
        void store.dispatch(getDeployment({ deploymentId, silent: true }));
      } else if (
        isTerminal &&
        (wasInProgress || prevStatusRef.current === null)
      ) {
        // One final fetch after completion (or if build finished before first poll)
        void store.dispatch(getDeployment({ deploymentId, silent: true }));
      }

      prevStatusRef.current = status;
    }, 3000);

    return () => {
      clearInterval(pollInterval);
      prevStatusRef.current = null;
    };
  }, [deploymentId]);
}

/**
 * Hook to subscribe to a deployment and get its data from Redux.
 * Handles tracking for Pusher subscriptions and fetches initial data.
 */
export function useDeploymentRealtime(deploymentId?: string | null) {
  const { trackDeployment, untrackDeployment } = useDeploymentsContext();

  // Select deployment data from Redux
  const deployment = useSelector(
    (state: RootState) => state.deployments.deployment.data,
  );
  const logs = useSelector(
    (state: RootState) => state.deployments.deployment.logs,
  );
  const pendingMigration = useSelector(
    (state: RootState) => state.deployments.deployment.pendingMigration,
  );
  const isLoading = useSelector(
    (state: RootState) => state.deployments.deployment.isLoading,
  );
  const error = useSelector(
    (state: RootState) => state.deployments.deployment.error,
  );

  // Track deployment for Pusher subscription
  useEffect(() => {
    if (!deploymentId) return;
    trackDeployment(deploymentId);
    return () => {
      untrackDeployment(deploymentId);
    };
  }, [deploymentId, trackDeployment, untrackDeployment]);

  // Always fetch on mount / when opening a deployment (including completed ones)
  useEffect(() => {
    if (!deploymentId) return;
    void store.dispatch(getDeployment({ deploymentId }));
  }, [deploymentId]);

  useDeploymentLogPolling(deploymentId);

  // Clear deployment when unmounting or deploymentId changes
  useEffect(() => {
    return () => {
      store.dispatch(clearDeployment());
    };
  }, [deploymentId]);

  // Only return data if it matches the requested deploymentId
  const matchesCurrentId = deployment?.id === deploymentId;

  return {
    deployment: matchesCurrentId ? deployment : null,
    logs: matchesCurrentId ? logs : null,
    pendingMigration: matchesCurrentId ? pendingMigration : null,
    isLoading,
    error,
  };
}

/**
 * Hook to stream deployment logs for the sidebar.
 * This is a simpler version that just ensures the deployment is tracked
 * and fetched - the sidebar reads directly from Redux state.
 */
export function useStreamDeploymentLogs(
  deploymentId: string | undefined,
): void {
  const { trackDeployment, untrackDeployment } = useDeploymentsContext();

  // Track deployment for Pusher subscription
  useEffect(() => {
    if (!deploymentId) return;
    trackDeployment(deploymentId);
    return () => {
      untrackDeployment(deploymentId);
    };
  }, [deploymentId, trackDeployment, untrackDeployment]);

  // Fetch deployment data when deploymentId changes
  useEffect(() => {
    if (!deploymentId) return;
    void store.dispatch(getDeployment({ deploymentId }));
  }, [deploymentId]);

  useDeploymentLogPolling(deploymentId);
}
